import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 7: CHANDNI - Moonlit Elegance
 * Style: Ethereal, moonlit, elegant with silver and indigo
 * Night-sky inspired luxury Indian fashion
 * Primary: #6366F1 (indigo-500), Background: #FFFFFF, Surface: #EEF2FF
 * Heading: Lora serif, Body: Inter sans-serif
 */
const Template7 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [currentSlide, setCurrentSlide] = useState(0);
    const touchStartX = useRef(0);

    // Hero Slides
    const heroSlides = [
        {
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200',
            subtitle: 'New Collection',
            title: 'Moonlit Elegance',
            description: 'Ethereal designs woven with starlight and tradition',
        },
        {
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200',
            subtitle: 'Bridal Season',
            title: 'Celestial Bridal',
            description: 'Where heritage meets the shimmer of midnight skies',
        },
        {
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=1200',
            subtitle: 'Festive Edit',
            title: 'Indigo Dreams',
            description: 'Luxurious silhouettes bathed in silver moonlight',
        },
    ];

    // Lookbook images
    const lookbook = [
        { image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600', title: 'The Silver Hour' },
        { image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=600', title: 'Twilight Grace' },
        { image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=600', title: 'Midnight Bloom' },
        { image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=600', title: 'Starlit Drape' },
    ];

    // Collections
    const collections = [
        {
            name: 'Evening Edit',
            description: 'Exquisite silhouettes for the woman who commands the night. From cocktail elegance to gala grandeur.',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Bridal Luminance',
            description: 'Where bridal tradition is reimagined under moonlight. Opulent, radiant, unforgettable.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Festive Glow',
            description: 'Celebrate every occasion with garments that capture the magic of starlit Indian nights.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    // Products
    const products = [
        { id: 1, name: 'Indigo Silk Evening Gown', price: '\u20B924,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Moonstone Banarasi Saree', price: '\u20B918,500', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Starlight Lehenga Set', price: '\u20B929,999', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Silver Zardozi Anarkali', price: '\u20B915,999', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Celestial Chanderi Dupatta', price: '\u20B93,999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Twilight Kundan Set', price: '\u20B912,499', image: 'https://images.pexels.com/photos/3819793/pexels-photo-3819793.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Midnight Velvet Clutch', price: '\u20B94,999', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Aurora Silk Scarf', price: '\u20B96,999', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The craftsmanship is like wearing moonlight itself. My bridal lehenga was nothing short of celestial. Every guest was mesmerised.',
            name: 'Priya Mehta',
            city: 'Mumbai',
            rating: 5,
        },
        {
            text: 'Chandni understands the art of making you feel like royalty under the stars. The evening gown I ordered exceeded every expectation.',
            name: 'Ananya Kapoor',
            city: 'Delhi',
            rating: 5,
        },
        {
            text: 'I have never worn anything so ethereal. The silk saree drapes like a silver dream and the embroidery work is unparalleled.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad',
            rating: 5,
        },
    ];

    // Auto-rotate hero slides
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    const goToSlide = (index) => setCurrentSlide(index);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@300;400;500&display=swap');
                @keyframes chandniFadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes chandniShimmer {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 1; }
                }
            `}</style>

            {/* ===== SECTION 1: CAROUSEL HERO ===== */}
            <section className="relative w-full h-screen overflow-hidden">
                {/* Slides */}
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className="absolute inset-0 transition-opacity duration-1000"
                        style={{ opacity: currentSlide === index ? 1 : 0, zIndex: currentSlide === index ? 1 : 0 }}
                    >
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover"
                        />
                        {/* Indigo Overlay */}
                        <div
                            className="absolute inset-0"
                            style={{
                                background: 'linear-gradient(to bottom, rgba(30, 27, 75, 0.5) 0%, rgba(30, 27, 75, 0.3) 40%, rgba(30, 27, 75, 0.7) 100%)',
                            }}
                        />
                    </div>
                ))}

                {/* Hero Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
                    <p
                        className="text-xs sm:text-sm tracking-[0.4em] uppercase mb-4 sm:mb-6"
                        style={{ color: '#A5B4FC', fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                    >
                        {heroSlides[currentSlide].subtitle}
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-light mb-4 sm:mb-6"
                        style={{
                            fontFamily: "'Lora', Georgia, serif",
                            color: '#FFFFFF',
                            letterSpacing: '0.1em',
                        }}
                    >
                        {heroSlides[currentSlide].title}
                    </h1>
                    <p
                        className="text-sm sm:text-base lg:text-lg font-light mb-8 sm:mb-10 max-w-lg mx-auto"
                        style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontFamily: "'Lora', Georgia, serif",
                            fontStyle: 'italic',
                        }}
                    >
                        {heroSlides[currentSlide].description}
                    </p>
                    <button
                        onClick={() => navigate('/template7/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300 rounded"
                        style={{
                            backgroundColor: '#6366F1',
                            border: '1px solid #6366F1',
                            color: '#FFFFFF',
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 500,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#4F46E5';
                            e.currentTarget.style.borderColor = '#4F46E5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#6366F1';
                            e.currentTarget.style.borderColor = '#6366F1';
                        }}
                    >
                        Explore Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Slide Arrows */}
                <button
                    onClick={prevSlide}
                    className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.6)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}
                >
                    <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.6)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}
                >
                    <ChevronRight className="w-5 h-5 text-white" />
                </button>

                {/* Dots */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className="transition-all duration-300 rounded-full"
                            style={{
                                width: currentSlide === index ? '32px' : '10px',
                                height: '10px',
                                backgroundColor: currentSlide === index ? '#6366F1' : 'rgba(255,255,255,0.4)',
                            }}
                        />
                    ))}
                </div>
            </section>

            {/* ===== SECTION 2: LOOKBOOK ===== */}
            <section className="py-16 sm:py-24 lg:py-32" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <p
                            className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#9CA3AF', fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                        >
                            Lookbook
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-light"
                            style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                        >
                            Moonlit Visions
                        </h2>
                    </div>

                    {/* Mobile: 2-col grid */}
                    <div className="grid grid-cols-2 gap-3 lg:hidden">
                        {lookbook.map((item, index) => (
                            <div
                                key={index}
                                className="relative overflow-hidden rounded-lg cursor-pointer group"
                                style={{ aspectRatio: index === 0 || index === 3 ? '3/4' : '1/1' }}
                                onClick={() => navigate('/template7/shop')}
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1E1B4B]/70 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3
                                        className="text-base font-light text-white"
                                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                                    >
                                        {item.title}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: Masonry-like grid */}
                    <div className="hidden lg:grid grid-cols-4 gap-6" style={{ gridAutoRows: '200px' }}>
                        {lookbook.map((item, index) => (
                            <div
                                key={index}
                                className="relative overflow-hidden rounded-lg cursor-pointer group"
                                style={{
                                    gridRow: index === 0 || index === 3 ? 'span 2' : 'span 1',
                                }}
                                onClick={() => navigate('/template7/shop')}
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1E1B4B]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                                    <h3
                                        className="text-xl font-light text-white mb-1"
                                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                                    >
                                        {item.title}
                                    </h3>
                                    <span className="text-xs tracking-[0.15em] uppercase text-white/70 inline-flex items-center gap-2">
                                        View <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: COLLECTIONS ===== */}
            {/* Mobile: stacked */}
            <section className="lg:hidden" style={{ backgroundColor: '#EEF2FF' }}>
                <div className="px-4 py-12">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-2 text-center"
                        style={{ color: '#6B7280' }}
                    >
                        Collections
                    </p>
                    <h2
                        className="text-2xl font-light mb-8 text-center"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        Our Curations
                    </h2>
                    <div className="flex flex-col gap-4">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template7/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group rounded-xl"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(30,27,75,0.85) 0%, rgba(30,27,75,0.3) 40%, transparent 100%)',
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3
                                        className="text-2xl font-light mb-2 text-white"
                                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2"
                                        style={{ color: '#A5B4FC' }}
                                    >
                                        Discover <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 3-col grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#EEF2FF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p
                        className="text-sm tracking-[0.3em] uppercase mb-3 text-center"
                        style={{ color: '#6B7280' }}
                    >
                        Collections
                    </p>
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        Our Curations
                    </h2>
                    <div className="grid grid-cols-3 gap-8">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template7/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group rounded-xl"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(30,27,75,0.85) 0%, rgba(30,27,75,0.3) 40%, transparent 100%)',
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <h3
                                        className="text-3xl font-light mb-3 text-white"
                                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-4 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300"
                                        style={{ color: '#A5B4FC' }}
                                    >
                                        Discover <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-12" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="px-4">
                    <h2
                        className="text-2xl font-light mb-8 text-center"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        Curated for You
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template7/shop')}
                                className="cursor-pointer group rounded-lg overflow-hidden"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#6366F1';
                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(99,102,241,0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#6366F1' : '#9CA3AF',
                                                fill: wishlist[product.id] ? '#6366F1' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <h3 className="text-xs font-normal line-clamp-1 mb-1" style={{ color: '#1E1B4B' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-xs font-medium" style={{ color: '#9CA3AF' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template7/shop')}
                        className="w-full mt-6 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300 rounded"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #6366F1',
                            color: '#6366F1',
                        }}
                    >
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop: 4-col grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        Curated for You
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template7/shop')}
                                className="cursor-pointer group rounded-lg overflow-hidden"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#6366F1';
                                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(99,102,241,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#6366F1' : '#9CA3AF',
                                                fill: wishlist[product.id] ? '#6366F1' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-normal mb-2 line-clamp-1" style={{ color: '#1E1B4B' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-sm font-medium" style={{ color: '#9CA3AF' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template7/shop')}
                            className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300 rounded"
                            style={{
                                backgroundColor: 'transparent',
                                border: '1px solid #6366F1',
                                color: '#6366F1',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#6366F1';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#6366F1';
                            }}
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: BRAND STORY ===== */}
            {/* Mobile */}
            <section className="lg:hidden" style={{ backgroundColor: '#EEF2FF' }}>
                <div className="w-full aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=800"
                        alt="Our Story"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 py-10">
                    <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#6B7280' }}>
                        Our Story
                    </p>
                    <h2
                        className="text-3xl font-light mb-5 leading-tight"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        Born Under Moonlight
                    </h2>
                    <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                        Chandni was born from a love letter to the Indian night sky. We believe that true elegance
                        reveals itself in the gentle glow of moonlight - subtle, radiant, and timeless. Our artisans
                        weave centuries of tradition with contemporary silhouettes, creating pieces that capture
                        the ethereal beauty of starlit evenings.
                    </p>
                    <button
                        onClick={() => navigate('/template7/about')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 text-sm tracking-[0.2em] uppercase transition-all duration-300 rounded"
                        style={{
                            backgroundColor: '#6366F1',
                            border: '1px solid #6366F1',
                            color: '#FFFFFF',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#4F46E5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#6366F1';
                        }}
                    >
                        Our Journey <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#EEF2FF' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-5 gap-12 items-center">
                        <div className="col-span-3 aspect-[4/3] overflow-hidden rounded-xl">
                            <img
                                src="https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                alt="Our Story"
                                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>
                        <div className="col-span-2">
                            <p className="text-sm tracking-[0.3em] uppercase mb-4" style={{ color: '#6B7280' }}>
                                Our Story
                            </p>
                            <h2
                                className="text-4xl xl:text-5xl font-light mb-8 leading-tight"
                                style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                            >
                                Born Under Moonlight
                            </h2>
                            <p className="leading-relaxed mb-6" style={{ color: '#1E1B4B' }}>
                                Chandni was born from a love letter to the Indian night sky. We believe that true elegance
                                reveals itself in the gentle glow of moonlight - subtle, radiant, and timeless.
                            </p>
                            <p className="text-sm leading-relaxed mb-10" style={{ color: '#6B7280' }}>
                                Our artisans weave centuries of tradition with contemporary silhouettes, creating pieces
                                that capture the ethereal beauty of starlit evenings. Each garment is a constellation
                                of craft, heritage, and modern grace.
                            </p>
                            <button
                                onClick={() => navigate('/template7/about')}
                                className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300 rounded"
                                style={{
                                    backgroundColor: '#6366F1',
                                    border: '1px solid #6366F1',
                                    color: '#FFFFFF',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#4F46E5';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#6366F1';
                                }}
                            >
                                Our Journey <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            <section className="py-16 sm:py-24 lg:py-32" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3" style={{ color: '#9CA3AF' }}>
                        Testimonials
                    </p>
                    <h2
                        className="text-3xl sm:text-4xl font-light mb-12 lg:mb-16"
                        style={{ fontFamily: "'Lora', Georgia, serif", color: '#1E1B4B' }}
                    >
                        What Our Clients Say
                    </h2>

                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        className="relative min-h-[200px]"
                    >
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="transition-all duration-500 absolute inset-0 flex flex-col items-center justify-center"
                                style={{
                                    opacity: activeTestimonial === index ? 1 : 0,
                                    pointerEvents: activeTestimonial === index ? 'auto' : 'none',
                                }}
                            >
                                <div className="flex gap-1 mb-6">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4" style={{ color: '#6366F1', fill: '#6366F1' }} />
                                    ))}
                                </div>
                                <p
                                    className="text-base sm:text-lg lg:text-xl leading-relaxed mb-6 max-w-2xl font-light"
                                    style={{
                                        fontFamily: "'Lora', Georgia, serif",
                                        color: '#1E1B4B',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    "{testimonial.text}"
                                </p>
                                <p className="text-sm font-medium" style={{ color: '#6366F1' }}>
                                    {testimonial.name}
                                </p>
                                <p className="text-xs" style={{ color: '#9CA3AF' }}>{testimonial.city}</p>
                            </div>
                        ))}
                    </div>

                    {/* Dots */}
                    <div className="flex justify-center gap-2 mt-8">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveTestimonial(index)}
                                className="rounded-full transition-all duration-300"
                                style={{
                                    width: activeTestimonial === index ? '24px' : '8px',
                                    height: '8px',
                                    backgroundColor: activeTestimonial === index ? '#6366F1' : '#E5E7EB',
                                }}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            <section
                className="py-16 sm:py-24 lg:py-32"
                style={{
                    background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4338CA 100%)',
                }}
            >
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p
                        className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-4"
                        style={{ color: '#A5B4FC' }}
                    >
                        Stay Connected
                    </p>
                    <h2
                        className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-white"
                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                    >
                        Join the Moonlit Circle
                    </h2>
                    <p
                        className="text-sm sm:text-base mb-10 font-light"
                        style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                    >
                        Be the first to discover new collections, exclusive offers, and behind-the-scenes stories from our atelier.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); setEmail(''); }}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-3.5 text-sm outline-none"
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(165, 180, 252, 0.3)',
                                borderRadius: '4px',
                                color: '#FFFFFF',
                                fontFamily: "'Inter', sans-serif",
                            }}
                        />
                        <button
                            type="submit"
                            className="px-8 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300 rounded"
                            style={{
                                backgroundColor: '#FFFFFF',
                                color: '#1E1B4B',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#EEF2FF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                            }}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template7;
